import React, { useState, useEffect } from 'react';
import Layout from '../components/Layout';
import { useSectionAccess } from '../hooks/useSectionAccess';
import { getAttendanceEditLogs, getAttendanceEditLogsSummary, getEmployees } from '../api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import {
  ClipboardList, Calendar, User, Clock, Filter, Download,
  ArrowRight, Edit3, AlertCircle
} from 'lucide-react';
import { toast } from 'sonner';

const AttendanceEditLogs = ({ user, setUser }) => {
  const [logs, setLogs] = useState([]);
  const [summary, setSummary] = useState(null);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterEmployeeId, setFilterEmployeeId] = useState('all');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    fetchLogs();
  }, [filterEmployeeId, dateFrom, dateTo]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [logsRes, summaryRes, empRes] = await Promise.all([
        getAttendanceEditLogs({}),
        getAttendanceEditLogsSummary(),
        getEmployees()
      ]);
      setLogs(logsRes.data);
      setSummary(summaryRes.data);
      setEmployees(empRes.data);
    } catch (error) {
      if (error.response?.status === 403) {
        toast.error('Access denied. Only Admin can view edit logs.');
      } else {
        toast.error('Failed to fetch attendance edit logs');
      }
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const fetchLogs = async () => {
    try {
      const params = {};
      if (filterEmployeeId !== 'all') params.employee_id = filterEmployeeId;
      if (dateFrom) params.date_from = dateFrom;
      if (dateTo) params.date_to = dateTo;
      
      const response = await getAttendanceEditLogs(params);
      setLogs(response.data);
    } catch (error) {
      console.error(error);
    }
  };

  const exportToCSV = () => {
    let csv = 'Date,Employee,Previous Status,New Status,Previous Clock In,New Clock In,Previous Clock Out,New Clock Out,Edit Reason,Edited By,Edited At\n';
    
    logs.forEach(log => {
      csv += `${log.date},"${log.employee_name}",${log.previous_values?.status || ''},${log.new_values?.status || ''},${log.previous_values?.clock_in || ''},${log.new_values?.clock_in || ''},${log.previous_values?.clock_out || ''},${log.new_values?.clock_out || ''},"${log.edit_reason}","${log.edited_by_name}",${log.edited_at}\n`;
    });
    
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `attendance_edit_logs_${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
    toast.success('Logs exported successfully');
  };

  const formatDateTime = (dateStr) => {
    if (!dateStr) return '-';
    const date = new Date(dateStr);
    return date.toLocaleString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusColor = (status) => {
    const colors = {
      present: 'bg-emerald-100 text-emerald-700',
      absent: 'bg-red-100 text-red-700',
      half_day: 'bg-amber-100 text-amber-700',
      on_leave: 'bg-blue-100 text-blue-700',
      holiday: 'bg-purple-100 text-purple-700',
      week_off: 'bg-gray-100 text-gray-700'
    };
    return colors[status] || 'bg-gray-100 text-gray-700';
  };

  const hasAccess = useSectionAccess('/attendance-edit-logs');
  if (hasAccess === false) {
    return (
      <Layout user={user} setUser={setUser}>
        <div className="flex flex-col items-center justify-center h-64">
          <AlertCircle className="h-16 w-16 text-red-500 mb-4" />
          <h2 className="text-xl font-semibold text-gray-900">Access Denied</h2>
          <p className="text-gray-500">You don't have permission to view this page.</p>
        </div>
      </Layout>
    );
  }

  if (loading) {
    return (
      <Layout user={user} setUser={setUser}>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout user={user} setUser={setUser}>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Attendance Edit Logs</h1>
            <p className="text-gray-500">Track all attendance modifications made by managers and admins</p>
          </div>
          <Button onClick={exportToCSV} className="flex items-center gap-2">
            <Download className="h-4 w-4" />
            Export CSV
          </Button>
        </div>

        {/* Summary Cards */}
        {summary && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Card className="bg-gradient-to-br from-blue-50 to-blue-100 border-blue-200">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-blue-600 font-medium">Total Edits</p>
                    <p className="text-3xl font-bold text-blue-900">{summary.total_edits}</p>
                  </div>
                  <Edit3 className="h-10 w-10 text-blue-500 opacity-50" />
                </div>
              </CardContent>
            </Card>
            
            <Card className="bg-gradient-to-br from-purple-50 to-purple-100 border-purple-200">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-purple-600 font-medium">Editors</p>
                    <p className="text-3xl font-bold text-purple-900">
                      {Object.keys(summary.edits_by_editor || {}).length}
                    </p>
                  </div>
                  <User className="h-10 w-10 text-purple-500 opacity-50" />
                </div>
              </CardContent>
            </Card>
            
            <Card className="bg-gradient-to-br from-amber-50 to-amber-100 border-amber-200">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-amber-600 font-medium">Days with Edits</p>
                    <p className="text-3xl font-bold text-amber-900">
                      {Object.keys(summary.edits_by_date || {}).length}
                    </p>
                  </div>
                  <Calendar className="h-10 w-10 text-amber-500 opacity-50" />
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Filters */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-lg flex items-center gap-2">
              <Filter className="h-5 w-5" />
              Filters
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div>
                <label className="text-sm font-medium text-gray-700 mb-1 block">Employee</label>
                <Select value={filterEmployeeId} onValueChange={setFilterEmployeeId}>
                  <SelectTrigger>
                    <SelectValue placeholder="All Employees" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Employees</SelectItem>
                    {employees.map(emp => (
                      <SelectItem key={emp.id} value={emp.id}>
                        {emp.first_name} {emp.last_name} ({emp.employee_id})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div>
                <label className="text-sm font-medium text-gray-700 mb-1 block">Attendance Date From</label>
                <Input
                  type="date"
                  value={dateFrom}
                  onChange={(e) => setDateFrom(e.target.value)}
                />
              </div>
              
              <div>
                <label className="text-sm font-medium text-gray-700 mb-1 block">Attendance Date To</label>
                <Input
                  type="date"
                  value={dateTo}
                  onChange={(e) => setDateTo(e.target.value)}
                />
              </div>
              
              <div className="flex items-end">
                <Button
                  variant="outline"
                  onClick={() => {
                    setFilterEmployeeId('all');
                    setDateFrom('');
                    setDateTo('');
                  }}
                >
                  Clear Filters
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Logs Table */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <ClipboardList className="h-5 w-5" />
              Edit History ({logs.length} records)
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            {logs.length === 0 ? (
              <div className="p-8 text-center text-gray-500">
                No attendance edit logs found.
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50 border-b">
                    <tr>
                      <th className="text-left p-3 text-sm font-medium text-gray-600">Attendance Date</th>
                      <th className="text-left p-3 text-sm font-medium text-gray-600">Employee</th>
                      <th className="text-left p-3 text-sm font-medium text-gray-600">Status Change</th>
                      <th className="text-left p-3 text-sm font-medium text-gray-600">Time Change</th>
                      <th className="text-left p-3 text-sm font-medium text-gray-600">Reason</th>
                      <th className="text-left p-3 text-sm font-medium text-gray-600">Edited By</th>
                      <th className="text-left p-3 text-sm font-medium text-gray-600">Edited At</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {logs.map(log => (
                      <tr key={log.id} className="hover:bg-gray-50">
                        <td className="p-3">
                          <div className="flex items-center gap-2">
                            <Calendar className="h-4 w-4 text-gray-400" />
                            <span className="font-medium">{log.date}</span>
                          </div>
                        </td>
                        <td className="p-3">
                          <div className="font-medium text-gray-900">{log.employee_name}</div>
                          <div className="text-xs text-gray-500">{log.employee_id}</div>
                        </td>
                        <td className="p-3">
                          {log.new_values?.status && (
                            <div className="flex items-center gap-2">
                              <span className={`px-2 py-1 rounded text-xs font-medium ${getStatusColor(log.previous_values?.status)}`}>
                                {log.previous_values?.status || 'N/A'}
                              </span>
                              <ArrowRight className="h-4 w-4 text-gray-400" />
                              <span className={`px-2 py-1 rounded text-xs font-medium ${getStatusColor(log.new_values?.status)}`}>
                                {log.new_values?.status}
                              </span>
                            </div>
                          )}
                          {!log.new_values?.status && <span className="text-gray-400">-</span>}
                        </td>
                        <td className="p-3 text-sm">
                          {(log.new_values?.clock_in || log.new_values?.clock_out) ? (
                            <div className="space-y-1">
                              {log.new_values?.clock_in && (
                                <div className="flex items-center gap-1">
                                  <span className="text-gray-500">In:</span>
                                  <span className="text-gray-400">{log.previous_values?.clock_in || 'N/A'}</span>
                                  <ArrowRight className="h-3 w-3 text-gray-400" />
                                  <span className="font-medium">{log.new_values?.clock_in}</span>
                                </div>
                              )}
                              {log.new_values?.clock_out && (
                                <div className="flex items-center gap-1">
                                  <span className="text-gray-500">Out:</span>
                                  <span className="text-gray-400">{log.previous_values?.clock_out || 'N/A'}</span>
                                  <ArrowRight className="h-3 w-3 text-gray-400" />
                                  <span className="font-medium">{log.new_values?.clock_out}</span>
                                </div>
                              )}
                            </div>
                          ) : (
                            <span className="text-gray-400">-</span>
                          )}
                        </td>
                        <td className="p-3">
                          <div className="max-w-[200px] truncate text-sm" title={log.edit_reason}>
                            {log.edit_reason}
                          </div>
                        </td>
                        <td className="p-3">
                          <div className="font-medium text-gray-900">{log.edited_by_name}</div>
                          <div className="text-xs text-gray-500 capitalize">{log.edited_by_role}</div>
                        </td>
                        <td className="p-3 text-sm text-gray-600">
                          {formatDateTime(log.edited_at)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
};

export default AttendanceEditLogs;
